// components/AttendanceTimeline/index.tsx

import { SettingsIcon } from '@chakra-ui/icons'
import {
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  VStack
} from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { formatDateTime, formatHour } from '../../utils/timezone'

export type NotesData = {
  id: number
  name?: string
  message: string
  created_by_name?: string
  event_date_format?: string
  event_date?: string
}

export type Scheduling = {
  id: number
  guide: number
  patient_id: number
  notes: Array<NotesData> | []
  equipments: Array<{
    id: number
    name: string
    group_name?: string
  }>
  equipment_reservations?: Array<{
    id: number
    equipment: number
    equipment_name: string
    equipment_group: number
    equipment_group_name: string
    start_time: string
    finish_time: string
    blocks_calendar: boolean
  }>
  edit_status: 1 | 2 | 3
  hash: string
  allowed_start_attendance: boolean
  block_checkout: boolean
  status_by_system: boolean
  scheduled_arrival_time: string
  scheduled_initial_time: string
  scheduled_final_time: string
  real_arrival_time: string
  real_initial_time: string
  real_final_time: string
  status: number
  professional_id: number
  patient_allow_note: boolean
  attendance_spot_id: number
  observation: string
  observation_payment: string
  value_to_income: number
  patient_number?: string
  invoice_number: number | null
  invoice_date: string | null
  block_guide: boolean
  is_active: boolean
  specialist_name: string | null
  patient_name: string | null
  patient_image: string | null
  sessions: Array<{
    id: number
    session_number: number
    status: number
    description: string | null
    attendance_id: number
    service_id: number
    service_name: string
    total_session: number
  }>
  sessions_not_performed: Array<{
    id: number
    session_number: number
    status: number
    description: string | null
    attendance_id: number
    service_id: number
    service_name: string
    total_session: number
  }>
  indications: Array<{
    id: number
  }>
  patient_social_name: string | null
  attendance_items_exclude?: Array<number>
  specialist_color: null | string
  ticket_status_payment?: number | null
  attendance_items: Array<{
    id: number
    service_name: string
    service_id: number
    total_session: number
  }>
  attendance_spot: {
    id: number
    index: number
    name: string
    is_active: boolean
  }
}

export type BlocksData = {
  id: number
  name: string
  description: string | null
  start_time: string | null
  finish_time: string | null
  is_recurrent: boolean | null
  week_day: string | null
  specialists: Array<{
    id: number
    name: string
    social_name: string
  }>
  created_at: string
  expand_recurrent_days: Array<string> | null
  recurrent_start_time: string | null
  recurrent_finish_time: string | null
}

interface AttendanceTimelineProps {
  attendances: Scheduling[]
  blocks?: BlocksData[]
  timeZone: string
  title?: string
  storageKey?: string
}

interface TimelineConfig {
  startHour: string
  finishHour: string
}

interface TimelineAppointmentSegment {
  attendance: Scheduling
  start: number
  end: number
}

interface TimelineBlockSegment {
  block: BlocksData
  start: number
  end: number
}

interface IntersectionSegment {
  start: number
  end: number
  attendances: Scheduling[]
}

interface StatusBadgeProps {
  status?: number | null
  t: (key: string) => string
}

const DEFAULT_STORAGE_KEY = '@CLINIK:ATTENDANCE_TIMELINE_CONFIG'

const DEFAULT_CONFIG: TimelineConfig = {
  startHour: '11:00',
  finishHour: '21:00'
}

const pad = (value: number) => String(value).padStart(2, '0')

const timeToMinutes = (time: string) => {
  if (!time) return 0

  const normalized = time.trim().replace('h', ':')
  const [hour = '0', minute = '0'] = normalized.split(':')

  return Number(hour) * 60 + Number(minute)
}

const formatMinutes = (minutes: number) => {
  const hour = Math.floor(minutes / 60)
  const minute = minutes % 60

  return `${pad(hour)}:${pad(minute)}`
}

const clamp = (value: number, min: number, max: number) => {
  return Math.min(Math.max(value, min), max)
}

const getPercent = (value: number, start: number, end: number) => {
  const total = end - start

  if (total <= 0) return 0

  return ((value - start) / total) * 100
}

const utcDateToMinutes = (date: string | null | undefined, timeZone: string) => {
  if (!date) return null

  const formattedHour = formatHour(date, timeZone)

  if (!formattedHour) return null

  return timeToMinutes(formattedHour)
}

const timeStringToMinutes = (value: string | null | undefined, timeZone: string) => {
  if (!value) return null

  const normalized = value.trim()

  if (!normalized) return null

  if (normalized.includes('T') || normalized.endsWith('Z')) {
    return utcDateToMinutes(normalized, timeZone)
  }

  return timeToMinutes(normalized)
}

const formatTimelineDateTime = (date: string | null | undefined, timeZone: string) => {
  if (!date) return '-'

  return formatDateTime(date, timeZone) || '-'
}

const formatBlockTime = (value: string | null | undefined, timeZone: string) => {
  if (!value) return '-'

  if (value.includes('T') || value.endsWith('Z')) {
    return formatTimelineDateTime(value, timeZone)
  }

  return value
}

const getAttendanceStart = (attendance: Scheduling, timeZone: string) => {
  return (
    utcDateToMinutes(attendance.scheduled_initial_time, timeZone) ??
    utcDateToMinutes(attendance.scheduled_arrival_time, timeZone) ??
    utcDateToMinutes(attendance.real_initial_time, timeZone) ??
    utcDateToMinutes(attendance.real_arrival_time, timeZone)
  )
}

const getAttendanceEnd = (attendance: Scheduling, timeZone: string) => {
  return (
    utcDateToMinutes(attendance.scheduled_final_time, timeZone) ??
    utcDateToMinutes(attendance.real_final_time, timeZone)
  )
}

const getBlockStart = (block: BlocksData, timeZone: string) => {
  return (
    timeStringToMinutes(block.start_time, timeZone) ??
    timeStringToMinutes(block.recurrent_start_time, timeZone)
  )
}

const getBlockEnd = (block: BlocksData, timeZone: string) => {
  return (
    timeStringToMinutes(block.finish_time, timeZone) ??
    timeStringToMinutes(block.recurrent_finish_time, timeZone)
  )
}

const buildHourMarks = (start: number, end: number) => {
  const marks: number[] = []
  const firstHour = Math.ceil(start / 60) * 60

  for (let current = firstHour; current <= end; current += 60) {
    marks.push(current)
  }

  return marks
}

const getStatusTextColor = (status?: number | null) => {
  const colors: Record<number, string> = {
    1: 'gray.700',
    2: 'blue.300',
    3: 'orange.500',
    4: 'red.500',
    5: 'blue.900',
    6: 'green.300',
    7: 'green.600'
  }

  return status ? colors[status] || 'gray.700' : 'gray.700'
}

const getStatusBgColor = (status?: number | null) => {
  const colors: Record<number, string> = {
    1: 'gray.50',
    2: 'blue.50',
    3: 'orange.50',
    4: 'red.50',
    5: 'blue.50',
    6: 'green.50',
    7: 'green.50'
  }

  return status ? colors[status] || 'gray.50' : 'gray.50'
}

const getStatusBorderColor = (status?: number | null) => {
  const colors: Record<number, string> = {
    1: 'gray.200',
    2: 'blue.100',
    3: 'orange.100',
    4: 'red.100',
    5: 'blue.200',
    6: 'green.100',
    7: 'green.200'
  }

  return status ? colors[status] || 'gray.200' : 'gray.200'
}

const getPaymentLabel = (status?: number | null) => {
  const labels: Record<number, string> = {
    1: 'Pendente',
    2: 'Pago',
    3: 'Parcial'
  }

  return status ? labels[status] || `Pagamento ${status}` : '-'
}

const getPaymentColor = (status?: number | null) => {
  const colors: Record<number, string> = {
    1: 'orange',
    2: 'green',
    3: 'yellow'
  }

  return status ? colors[status] || 'gray' : 'gray'
}

const getEditStatusLabel = (status: 1 | 2 | 3) => {
  const labels: Record<1 | 2 | 3, string> = {
    1: 'Livre para edição',
    2: 'Edição controlada',
    3: 'Bloqueado'
  }

  return labels[status]
}

const getBlockLabel = (block: BlocksData) => {
  if (block.is_recurrent) {
    return `${block.name} · Recorrente`
  }

  return block.name
}

const StatusBadge = ({ status, t }: StatusBadgeProps) => {
  return (
    <Badge
      bg={getStatusBgColor(status)}
      color={getStatusTextColor(status)}
      border="1px solid"
      borderColor={getStatusBorderColor(status)}
      borderRadius="full"
      px={2}
      fontWeight="extrabold"
    >
      {status ? t(`status.${status}`) : '-'}
    </Badge>
  )
}

const buildAppointmentSegments = (
  attendances: Scheduling[],
  start: number,
  end: number,
  timeZone: string
): TimelineAppointmentSegment[] => {
  return attendances
    .map((attendance) => {
      const attendanceStart = getAttendanceStart(attendance, timeZone)
      const attendanceEnd = getAttendanceEnd(attendance, timeZone)

      if (attendanceStart === null || attendanceEnd === null) return null
      if (attendanceEnd <= attendanceStart) return null
      if (attendanceEnd <= start || attendanceStart >= end) return null

      return {
        attendance,
        start: clamp(attendanceStart, start, end),
        end: clamp(attendanceEnd, start, end)
      }
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (!a || !b) return 0

      return a.start - b.start
    }) as TimelineAppointmentSegment[]
}

const buildBlockSegments = (
  blocks: BlocksData[],
  start: number,
  end: number,
  timeZone: string
): TimelineBlockSegment[] => {
  return blocks
    .map((block) => {
      const blockStart = getBlockStart(block, timeZone)
      const blockEnd = getBlockEnd(block, timeZone)

      if (blockStart === null || blockEnd === null) return null
      if (blockEnd <= blockStart) return null
      if (blockEnd <= start || blockStart >= end) return null

      return {
        block,
        start: clamp(blockStart, start, end),
        end: clamp(blockEnd, start, end)
      }
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (!a || !b) return 0

      return a.start - b.start
    }) as TimelineBlockSegment[]
}

/**
 * Monta interseções reais de atendimentos.
 *
 * Diferente de comparar pares isolados, esta função identifica todos
 * os atendimentos ativos dentro de cada intervalo. Assim, se houver
 * três atendimentos no mesmo período, o modal recebe os três.
 */
const buildIntersectionSegments = (
  appointments: TimelineAppointmentSegment[]
): IntersectionSegment[] => {
  if (appointments.length < 2) return []

  const boundaries = Array.from(
    new Set(appointments.flatMap((appointment) => [appointment.start, appointment.end]))
  ).sort((a, b) => a - b)

  const intersections: IntersectionSegment[] = []

  for (let index = 0; index < boundaries.length - 1; index += 1) {
    const segmentStart = boundaries[index]
    const segmentEnd = boundaries[index + 1]

    if (segmentStart >= segmentEnd) continue

    const midpoint = segmentStart + (segmentEnd - segmentStart) / 2

    const activeAppointments = appointments.filter(
      (appointment) => appointment.start <= midpoint && appointment.end >= midpoint
    )

    if (activeAppointments.length < 2) continue

    const activeAttendances = activeAppointments.map((appointment) => appointment.attendance)

    const previousIntersection = intersections[intersections.length - 1]

    const currentIds = activeAttendances
      .map((attendance) => attendance.id)
      .sort((a, b) => a - b)
      .join(',')

    const previousIds = previousIntersection?.attendances
      .map((attendance) => attendance.id)
      .sort((a, b) => a - b)
      .join(',')

    if (
      previousIntersection &&
      previousIntersection.end === segmentStart &&
      previousIds === currentIds
    ) {
      previousIntersection.end = segmentEnd
      continue
    }

    intersections.push({
      start: segmentStart,
      end: segmentEnd,
      attendances: activeAttendances
    })
  }

  return intersections
}

const AttendanceDetailsModal = ({
  attendance,
  timeZone,
  isOpen,
  onClose,
  t
}: {
  attendance: Scheduling | null
  timeZone: string
  isOpen: boolean
  onClose: () => void
  t: (key: string) => string
}) => {
  if (!attendance) return null

  const services = attendance.attendance_items || []
  const notes = attendance.notes || []
  const equipments = attendance.equipment_reservations?.length
    ? attendance.equipment_reservations.map(item => ({ id: item.id, name: item.equipment_name, group_name: item.equipment_group_name }))
    : attendance.equipments || []
  const sessions = attendance.sessions || []
  const sessionsNotPerformed = attendance.sessions_not_performed || []

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
      <ModalOverlay />

      <ModalContent borderRadius="2xl" overflow="hidden">
        <ModalHeader bg="blue.500" color="white" py={3}>
          <Flex align="center" gap={3}>
            <Avatar
              size="sm"
              src={attendance.patient_image || ''}
              name={attendance.patient_social_name || attendance.patient_name || 'Paciente'}
              border="2px solid"
              borderColor="whiteAlpha.700"
            />

            <Box minW={0}>
              <Text fontSize="md" fontWeight="bold" noOfLines={1}>
                {attendance.patient_social_name ||
                  attendance.patient_name ||
                  'Paciente não informado'}
              </Text>

              {attendance.patient_social_name && attendance.patient_name && (
                <Text fontSize="xs" opacity={0.9} noOfLines={1}>
                  {attendance.patient_name}
                </Text>
              )}
            </Box>
          </Flex>
        </ModalHeader>

        <ModalCloseButton color="white" />

        <ModalBody bg="white" p={4}>
          <VStack align="stretch" spacing={4}>
            <HStack flexWrap="wrap">
              <StatusBadge status={attendance.status} t={t} />

              <Badge colorScheme={getPaymentColor(attendance.ticket_status_payment)}>
                {getPaymentLabel(attendance.ticket_status_payment)}
              </Badge>

              <Badge colorScheme="blue" variant="outline">
                Guia {attendance.guide}
              </Badge>

              <Badge colorScheme="purple" variant="outline">
                {attendance.attendance_spot?.name || 'Sem sala'}
              </Badge>
            </HStack>

            <Box borderWidth="1px" borderColor="gray.100" borderRadius="xl" p={3} bg="gray.50">
              <Text fontSize="sm" color="gray.600">
                <strong>Especialista:</strong> {attendance.specialist_name || '-'}
              </Text>

              <Text fontSize="sm" color="gray.600">
                <strong>Telefone:</strong> {attendance.patient_number || '-'}
              </Text>

              <Text fontSize="sm" color="gray.600">
                <strong>Status de edição:</strong> {getEditStatusLabel(attendance.edit_status)}
              </Text>

              {attendance.observation_payment && (
                <Text fontSize="sm" color="gray.600">
                  <strong>Obs. pagamento:</strong> {attendance.observation_payment}
                </Text>
              )}
            </Box>

            <Box borderWidth="1px" borderColor="blue.100" borderRadius="xl" p={3} bg="blue.50">
              <Text fontWeight="bold" fontSize="sm" color="blue.700" mb={2}>
                Horários
              </Text>

              <Text fontSize="sm" color="gray.700">
                <strong>Chegada prevista:</strong>{' '}
                {formatTimelineDateTime(attendance.scheduled_arrival_time, timeZone)}
              </Text>

              <Text fontSize="sm" color="gray.700">
                <strong>Início previsto:</strong>{' '}
                {formatTimelineDateTime(attendance.scheduled_initial_time, timeZone)}
              </Text>

              <Text fontSize="sm" color="gray.700">
                <strong>Fim previsto:</strong>{' '}
                {formatTimelineDateTime(attendance.scheduled_final_time, timeZone)}
              </Text>

              <Divider my={2} />

              <Text fontSize="sm" color="gray.700">
                <strong>Chegada real:</strong>{' '}
                {formatTimelineDateTime(attendance.real_arrival_time, timeZone)}
              </Text>

              <Text fontSize="sm" color="gray.700">
                <strong>Início real:</strong>{' '}
                {formatTimelineDateTime(attendance.real_initial_time, timeZone)}
              </Text>

              <Text fontSize="sm" color="gray.700">
                <strong>Fim real:</strong>{' '}
                {formatTimelineDateTime(attendance.real_final_time, timeZone)}
              </Text>
            </Box>

            <Box>
              <Text fontWeight="bold" fontSize="sm" color="blue.600" mb={2}>
                Serviços
              </Text>

              {services.length ? (
                <VStack align="stretch" spacing={2}>
                  {services.map((item) => (
                    <Box
                      key={item.id}
                      p={2}
                      bg="gray.50"
                      borderRadius="lg"
                      borderWidth="1px"
                      borderColor="gray.100"
                    >
                      <Text fontSize="sm" color="gray.700">
                        {item.service_name}
                      </Text>

                      <Text fontSize="xs" color="gray.500">
                        {item.total_session} sessão
                      </Text>
                    </Box>
                  ))}
                </VStack>
              ) : (
                <Text fontSize="sm" color="gray.400">
                  Nenhum serviço informado.
                </Text>
              )}
            </Box>

            {sessions.length ? (
              <Box>
                <Text fontWeight="bold" fontSize="sm" color="blue.600" mb={2}>
                  Sessões realizadas
                </Text>

                <VStack align="stretch" spacing={2}>
                  {sessions.slice(0, 4).map((session) => (
                    <Box
                      key={session.id}
                      p={2}
                      bg="gray.50"
                      borderRadius="lg"
                      borderWidth="1px"
                      borderColor="gray.100"
                    >
                      <Text fontSize="sm" color="gray.700">
                        Sessão {session.session_number}: {session.service_name}
                      </Text>

                      {session.description && (
                        <Text fontSize="xs" color="gray.500">
                          {session.description}
                        </Text>
                      )}
                    </Box>
                  ))}
                </VStack>
              </Box>
            ) : null}

            {sessionsNotPerformed.length ? (
              <Box>
                <Text fontWeight="bold" fontSize="sm" color="orange.500" mb={2}>
                  Sessões não realizadas
                </Text>

                <VStack align="stretch" spacing={2}>
                  {sessionsNotPerformed.slice(0, 4).map((session) => (
                    <Box
                      key={session.id}
                      p={2}
                      bg="orange.50"
                      borderRadius="lg"
                      borderWidth="1px"
                      borderColor="orange.100"
                    >
                      <Text fontSize="sm" color="gray.700">
                        Sessão {session.session_number}: {session.service_name}
                      </Text>

                      {session.description && (
                        <Text fontSize="xs" color="gray.500">
                          {session.description}
                        </Text>
                      )}
                    </Box>
                  ))}
                </VStack>
              </Box>
            ) : null}

            {equipments.length ? (
              <Box>
                <Text fontWeight="bold" fontSize="sm" color="blue.600" mb={2}>
                  Equipamentos
                </Text>

                <HStack flexWrap="wrap">
                  {equipments.map((equipment) => (
                    <Badge key={equipment.id} colorScheme="blue">
                      {equipment.group_name ? `${equipment.group_name}: ` : ''}{equipment.name}
                    </Badge>
                  ))}
                </HStack>
              </Box>
            ) : null}

            {notes.length ? (
              <Box>
                <Text fontWeight="bold" fontSize="sm" color="blue.600" mb={2}>
                  Anotações
                </Text>

                <VStack align="stretch" spacing={2}>
                  {notes.slice(0, 3).map((note) => (
                    <Box
                      key={note.id}
                      p={2}
                      bg="blue.50"
                      borderRadius="lg"
                      borderWidth="1px"
                      borderColor="blue.100"
                    >
                      <Text fontSize="sm" color="gray.700">
                        {note.message}
                      </Text>

                      <HStack mt={1} spacing={2}>
                        {note.event_date_format && (
                          <Text fontSize="xs" color="gray.500">
                            {note.event_date_format}
                          </Text>
                        )}

                        {note.created_by_name && (
                          <Text fontSize="xs" color="gray.500">
                            por {note.created_by_name}
                          </Text>
                        )}
                      </HStack>
                    </Box>
                  ))}
                </VStack>
              </Box>
            ) : null}

            {attendance.observation ? (
              <Box>
                <Text fontWeight="bold" fontSize="sm" color="blue.600" mb={2}>
                  Observação
                </Text>

                <Box p={3} bg="gray.50" borderRadius="xl" borderWidth="1px" borderColor="gray.100">
                  <Text fontSize="sm" color="gray.600">
                    {attendance.observation}
                  </Text>
                </Box>
              </Box>
            ) : null}
          </VStack>
        </ModalBody>

        <ModalFooter py={3}>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

const IntersectionDetailsModal = ({
  intersection,
  timeZone,
  isOpen,
  onClose,
  t
}: {
  intersection: IntersectionSegment | null
  timeZone: string
  isOpen: boolean
  onClose: () => void
  t: (key: string) => string
}) => {
  if (!intersection) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
      <ModalOverlay />

      <ModalContent borderRadius="2xl" overflow="hidden">
        <ModalHeader bg="red.500" color="white" py={3}>
          Interseção de atendimentos
        </ModalHeader>

        <ModalCloseButton color="white" />

        <ModalBody bg="white" p={4}>
          <VStack align="stretch" spacing={4}>
            <Box bg="red.50" borderRadius="xl" p={3} borderWidth="1px" borderColor="red.100">
              <Text fontWeight="bold" color="red.600" fontSize="sm">
                Horário em conflito
              </Text>

              <Text fontSize="sm" color="gray.700">
                {formatMinutes(intersection.start)} até {formatMinutes(intersection.end)}
              </Text>
            </Box>

            {intersection.attendances.map((attendance) => (
              <Box
                key={attendance.id}
                borderWidth="1px"
                borderColor="gray.100"
                borderRadius="xl"
                p={3}
                bg="gray.50"
              >
                <Flex gap={3} align="center" mb={3}>
                  <Avatar
                    size="sm"
                    src={attendance.patient_image || ''}
                    name={attendance.patient_social_name || attendance.patient_name || 'Paciente'}
                  />

                  <Box minW={0} flex="1">
                    <Text fontWeight="bold" color="gray.700" noOfLines={1}>
                      {attendance.patient_social_name ||
                        attendance.patient_name ||
                        `Atendimento ${attendance.id}`}
                    </Text>

                    {attendance.patient_social_name && attendance.patient_name && (
                      <Text fontSize="xs" color="gray.500" noOfLines={1}>
                        {attendance.patient_name}
                      </Text>
                    )}
                  </Box>

                  <StatusBadge status={attendance.status} t={t} />
                </Flex>

                <HStack flexWrap="wrap" mb={2}>
                  <Badge colorScheme="blue" variant="outline">
                    Guia {attendance.guide}
                  </Badge>

                  <Badge colorScheme="purple" variant="outline">
                    {attendance.attendance_spot?.name || 'Sem sala'}
                  </Badge>

                  <Badge colorScheme="gray" variant="outline">
                    {attendance.specialist_name || 'Sem especialista'}
                  </Badge>
                </HStack>

                <Box bg="white" borderRadius="lg" p={3} borderWidth="1px" borderColor="gray.100">
                  <Text fontSize="sm" color="gray.700">
                    <strong>Chegada prevista:</strong>{' '}
                    {formatTimelineDateTime(attendance.scheduled_arrival_time, timeZone)}
                  </Text>

                  <Text fontSize="sm" color="gray.700">
                    <strong>Início previsto:</strong>{' '}
                    {formatTimelineDateTime(attendance.scheduled_initial_time, timeZone)}
                  </Text>

                  <Text fontSize="sm" color="gray.700">
                    <strong>Fim previsto:</strong>{' '}
                    {formatTimelineDateTime(attendance.scheduled_final_time, timeZone)}
                  </Text>

                  <Divider my={2} />

                  <Text fontSize="sm" color="gray.700">
                    <strong>Chegada real:</strong>{' '}
                    {formatTimelineDateTime(attendance.real_arrival_time, timeZone)}
                  </Text>

                  <Text fontSize="sm" color="gray.700">
                    <strong>Início real:</strong>{' '}
                    {formatTimelineDateTime(attendance.real_initial_time, timeZone)}
                  </Text>

                  <Text fontSize="sm" color="gray.700">
                    <strong>Fim real:</strong>{' '}
                    {formatTimelineDateTime(attendance.real_final_time, timeZone)}
                  </Text>
                </Box>
              </Box>
            ))}
          </VStack>
        </ModalBody>

        <ModalFooter py={3}>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

const BlockDetailsModal = ({
  block,
  timeZone,
  isOpen,
  onClose
}: {
  block: BlocksData | null
  timeZone: string
  isOpen: boolean
  onClose: () => void
}) => {
  if (!block) return null

  const startTime = block.start_time || block.recurrent_start_time
  const finishTime = block.finish_time || block.recurrent_finish_time

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />

      <ModalContent borderRadius="2xl" overflow="hidden">
        <ModalHeader bg="red.500" color="white" py={3}>
          Bloqueio de horário
        </ModalHeader>

        <ModalCloseButton color="white" />

        <ModalBody bg="white" p={4}>
          <VStack align="stretch" spacing={4}>
            <Box>
              <Text fontSize="lg" fontWeight="bold" color="gray.700">
                {block.name}
              </Text>

              {block.description && (
                <Text fontSize="sm" color="gray.500" mt={1}>
                  {block.description}
                </Text>
              )}
            </Box>

            <Box bg="red.50" borderRadius="xl" p={3} borderWidth="1px" borderColor="red.100">
              <Text fontWeight="bold" color="red.600" fontSize="sm" mb={2}>
                Horário do bloqueio
              </Text>

              <Text fontSize="sm" color="gray.700">
                <strong>Início:</strong> {formatBlockTime(startTime, timeZone)}
              </Text>

              <Text fontSize="sm" color="gray.700">
                <strong>Fim:</strong> {formatBlockTime(finishTime, timeZone)}
              </Text>

              <Text fontSize="sm" color="gray.700">
                <strong>Recorrente:</strong> {block.is_recurrent ? 'Sim' : 'Não'}
              </Text>

              {block.week_day && (
                <Text fontSize="sm" color="gray.700">
                  <strong>Dias da semana:</strong> {block.week_day}
                </Text>
              )}
            </Box>

            {block.expand_recurrent_days?.length ? (
              <Box>
                <Text fontWeight="bold" fontSize="sm" color="red.600" mb={2}>
                  Dias recorrentes
                </Text>

                <HStack flexWrap="wrap">
                  {block.expand_recurrent_days.map((day) => (
                    <Badge key={day} colorScheme="red" variant="subtle">
                      {day}
                    </Badge>
                  ))}
                </HStack>
              </Box>
            ) : null}

            {block.specialists?.length ? (
              <Box>
                <Text fontWeight="bold" fontSize="sm" color="red.600" mb={2}>
                  Especialistas
                </Text>

                <VStack align="stretch" spacing={2}>
                  {block.specialists.map((specialist) => (
                    <Box
                      key={specialist.id}
                      p={2}
                      bg="gray.50"
                      borderRadius="lg"
                      borderWidth="1px"
                      borderColor="gray.100"
                    >
                      <Text fontSize="sm" color="gray.700">
                        {specialist.social_name || specialist.name}
                      </Text>

                      {specialist.social_name && (
                        <Text fontSize="xs" color="gray.500">
                          {specialist.name}
                        </Text>
                      )}
                    </Box>
                  ))}
                </VStack>
              </Box>
            ) : null}

            <Text fontSize="xs" color="gray.400">
              Criado em: {block.created_at}
            </Text>
          </VStack>
        </ModalBody>

        <ModalFooter py={3}>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export const AttendanceTimeline = ({
  attendances,
  blocks = [],
  timeZone,
  storageKey = DEFAULT_STORAGE_KEY,
  title = 'Timeline de atendimentos'
}: AttendanceTimelineProps) => {
  const [t] = useTranslation('pageScheduling')

  const { isOpen, onOpen, onClose } = useDisclosure()

  const {
    isOpen: isAttendanceDetailsOpen,
    onOpen: onAttendanceDetailsOpen,
    onClose: onAttendanceDetailsClose
  } = useDisclosure()

  const {
    isOpen: isIntersectionDetailsOpen,
    onOpen: onIntersectionDetailsOpen,
    onClose: onIntersectionDetailsClose
  } = useDisclosure()

  const {
    isOpen: isBlockDetailsOpen,
    onOpen: onBlockDetailsOpen,
    onClose: onBlockDetailsClose
  } = useDisclosure()

  const [config, setConfig] = useState<TimelineConfig>(DEFAULT_CONFIG)

  const [draftConfig, setDraftConfig] = useState<TimelineConfig>(DEFAULT_CONFIG)

  const [selectedAttendance, setSelectedAttendance] = useState<Scheduling | null>(null)

  const [selectedIntersection, setSelectedIntersection] = useState<IntersectionSegment | null>(null)

  const [selectedBlock, setSelectedBlock] = useState<BlocksData | null>(null)

  useEffect(() => {
    const savedConfig = localStorage.getItem(storageKey)

    if (!savedConfig) return

    try {
      const parsed = JSON.parse(savedConfig)

      const nextConfig = {
        startHour: parsed.startHour || DEFAULT_CONFIG.startHour,
        finishHour: parsed.finishHour || DEFAULT_CONFIG.finishHour
      }

      setConfig(nextConfig)
      setDraftConfig(nextConfig)
    } catch {
      setConfig(DEFAULT_CONFIG)
      setDraftConfig(DEFAULT_CONFIG)
    }
  }, [storageKey])

  useEffect(() => {
    setDraftConfig(config)
  }, [config])

  const startMinutes = useMemo(() => timeToMinutes(config.startHour), [config.startHour])

  const finishMinutes = useMemo(() => timeToMinutes(config.finishHour), [config.finishHour])

  const isInvalidConfig = finishMinutes <= startMinutes

  const hourMarks = useMemo(() => {
    if (isInvalidConfig) return []

    return buildHourMarks(startMinutes, finishMinutes)
  }, [startMinutes, finishMinutes, isInvalidConfig])

  const appointmentSegments = useMemo(() => {
    if (isInvalidConfig) return []

    return buildAppointmentSegments(attendances, startMinutes, finishMinutes, timeZone)
  }, [attendances, startMinutes, finishMinutes, timeZone, isInvalidConfig])

  const blockSegments = useMemo(() => {
    if (isInvalidConfig) return []

    return buildBlockSegments(blocks, startMinutes, finishMinutes, timeZone)
  }, [blocks, startMinutes, finishMinutes, timeZone, isInvalidConfig])

  const intersections = useMemo(() => {
    return buildIntersectionSegments(appointmentSegments)
  }, [appointmentSegments])

  const handleOpenAttendanceDetails = (attendance: Scheduling) => {
    setSelectedAttendance(attendance)
    onAttendanceDetailsOpen()
  }

  const handleCloseAttendanceDetails = () => {
    onAttendanceDetailsClose()
    setSelectedAttendance(null)
  }

  const handleOpenIntersectionDetails = (intersection: IntersectionSegment) => {
    setSelectedIntersection(intersection)
    onIntersectionDetailsOpen()
  }

  const handleCloseIntersectionDetails = () => {
    onIntersectionDetailsClose()
    setSelectedIntersection(null)
  }

  const handleOpenBlockDetails = (block: BlocksData) => {
    setSelectedBlock(block)
    onBlockDetailsOpen()
  }

  const handleCloseBlockDetails = () => {
    onBlockDetailsClose()
    setSelectedBlock(null)
  }

  const handleSaveConfig = () => {
    const draftStart = timeToMinutes(draftConfig.startHour)
    const draftFinish = timeToMinutes(draftConfig.finishHour)

    if (draftFinish <= draftStart) return

    setConfig(draftConfig)

    localStorage.setItem(storageKey, JSON.stringify(draftConfig))

    onClose()
  }

  return (
    <Box
      bg="white"
      borderRadius="2xl"
      borderWidth="1px"
      borderColor="gray.100"
      p={3}
      boxShadow="0 8px 24px rgba(15, 23, 42, 0.04)"
    >
      <Flex justify="space-between" align="center" mb={2} gap={3}>
        <HStack spacing={2} minW={0}>
          <Text fontWeight="semibold" fontSize="sm" color="gray.700" noOfLines={1}>
            {title}
          </Text>

          <Badge colorScheme="blue" variant="subtle" borderRadius="full" px={2}>
            {config.startHour} - {config.finishHour}
          </Badge>
        </HStack>

        <HStack spacing={3} mb={2} fontSize="xs" color="gray.500" flexWrap="wrap">
          <HStack spacing={1}>
            <Box w="8px" h="8px" bg="green.100" borderRadius="full" />
            <Text>Livre</Text>
          </HStack>

          <HStack spacing={1}>
            <Box w="8px" h="8px" bg="gray.200" borderRadius="full" />
            <Text>Atendimento</Text>
          </HStack>

          <HStack spacing={1}>
            <Box w="8px" h="8px" bg="red.500" borderRadius="full" />
            <Text>Bloqueio</Text>
          </HStack>

          <HStack spacing={1}>
            <Box w="8px" h="8px" bg="red.400" borderRadius="full" />
            <Text>Interseção</Text>
          </HStack>
        </HStack>

        <IconButton
          aria-label="Configurar timeline"
          icon={<SettingsIcon />}
          size="xs"
          colorScheme="blue"
          variant="ghost"
          borderRadius="full"
          onClick={onOpen}
        />
      </Flex>
      {isInvalidConfig ? (
        <Box bg="red.50" color="red.600" p={3} borderRadius="xl" fontSize="sm">
          O horário final precisa ser maior que o horário inicial.
        </Box>
      ) : (
        <Box overflowX="auto" pb={1}>
          <Box minW="980px">
            <Box position="relative" h="24px" mb={1}>
              {hourMarks.map((mark) => {
                const left = getPercent(mark, startMinutes, finishMinutes)

                return (
                  <Box
                    key={mark}
                    position="absolute"
                    left={`${left}%`}
                    transform="translateX(-50%)"
                    textAlign="center"
                  >
                    <Text fontSize="xs" fontWeight="medium" color="gray.500">
                      {formatMinutes(mark)}
                    </Text>

                    <Box h="7px" w="1px" bg="gray.300" mx="auto" mt={1} />
                  </Box>
                )
              })}
            </Box>

            <Box
              position="relative"
              h="38px"
              border="1px solid"
              borderColor="gray.200"
              bg="green.100"
              overflow="hidden"
              borderRadius="xl"
            >
              {/* Base verde: representa horário livre */}

              {/* Bloqueios: ficam acima do horário livre */}
              {blockSegments.map((segment, index) => {
                const left = getPercent(segment.start, startMinutes, finishMinutes)

                const width = getPercent(segment.end, startMinutes, finishMinutes) - left

                return (
                  <Box
                    key={`block-${segment.block.id}-${index}`}
                    position="absolute"
                    left={`${left}%`}
                    top={0}
                    h="100%"
                    w={`${width}%`}
                    bg="red.500"
                    cursor="pointer"
                    zIndex={1}
                    borderRight="1px solid"
                    borderColor="red.600"
                    transition="0.2s"
                    onClick={() => handleOpenBlockDetails(segment.block)}
                    _hover={{
                      bg: 'red.600'
                    }}
                  >
                    <Flex h="100%" align="center" px={2} overflow="hidden">
                      <Text fontSize="xs" fontWeight="bold" color="white" noOfLines={1}>
                        {getBlockLabel(segment.block)}
                      </Text>
                    </Flex>
                  </Box>
                )
              })}

              {/* Atendimentos: ficam sobre bloqueios quando houver sobreposição */}
              {appointmentSegments.map((segment) => {
                const left = getPercent(segment.start, startMinutes, finishMinutes)

                const width = getPercent(segment.end, startMinutes, finishMinutes) - left

                const { attendance } = segment

                return (
                  <Box
                    key={`attendance-${attendance.id}`}
                    position="absolute"
                    left={`${left}%`}
                    top={0}
                    h="100%"
                    w={`${width}%`}
                    bg="gray.200"
                    borderRight="1px solid"
                    borderColor="white"
                    cursor="pointer"
                    transition="0.2s"
                    zIndex={2}
                    onClick={() => handleOpenAttendanceDetails(attendance)}
                    _hover={{
                      bg: getStatusBgColor(attendance.status),
                      transform: 'scaleY(1.04)'
                    }}
                  >
                    <Flex
                      h="100%"
                      align="center"
                      px={2}
                      overflow="hidden"
                      borderLeft="4px solid"
                      borderLeftColor={
                        attendance.specialist_color || getStatusTextColor(attendance.status)
                      }
                    >
                      <Text
                        fontSize="xs"
                        fontWeight="extrabold"
                        color={getStatusTextColor(attendance.status)}
                        noOfLines={1}
                      >
                        {attendance.patient_social_name ||
                          attendance.patient_name ||
                          `Atendimento ${attendance.id}`}
                      </Text>
                    </Flex>
                  </Box>
                )
              })}

              {/* Interseções entre atendimentos: camada superior */}
              {intersections.map((intersection, index) => {
                const left = getPercent(intersection.start, startMinutes, finishMinutes)

                const width = getPercent(intersection.end, startMinutes, finishMinutes) - left

                return (
                  <Box
                    key={`intersection-${index}`}
                    position="absolute"
                    left={`${left}%`}
                    top={0}
                    h="100%"
                    w={`${width}%`}
                    bg="red.400"
                    opacity={0.9}
                    cursor="pointer"
                    zIndex={4}
                    transition="0.2s"
                    onClick={() => handleOpenIntersectionDetails(intersection)}
                    _hover={{
                      bg: 'red.500',
                      opacity: 1
                    }}
                  />
                )
              })}
            </Box>
          </Box>
        </Box>
      )}

      <AttendanceDetailsModal
        attendance={selectedAttendance}
        timeZone={timeZone}
        isOpen={isAttendanceDetailsOpen}
        onClose={handleCloseAttendanceDetails}
        t={t}
      />

      <IntersectionDetailsModal
        intersection={selectedIntersection}
        timeZone={timeZone}
        isOpen={isIntersectionDetailsOpen}
        onClose={handleCloseIntersectionDetails}
        t={t}
      />

      <BlockDetailsModal
        block={selectedBlock}
        timeZone={timeZone}
        isOpen={isBlockDetailsOpen}
        onClose={handleCloseBlockDetails}
      />

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />

        <ModalContent borderRadius="2xl">
          <ModalHeader>Configurar timeline</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Horário inicial</FormLabel>

                <Input
                  type="time"
                  value={draftConfig.startHour}
                  onChange={(event) =>
                    setDraftConfig((previous) => ({
                      ...previous,
                      startHour: event.target.value
                    }))
                  }
                />
              </FormControl>

              <FormControl>
                <FormLabel>Horário final</FormLabel>

                <Input
                  type="time"
                  value={draftConfig.finishHour}
                  onChange={(event) =>
                    setDraftConfig((previous) => ({
                      ...previous,
                      finishHour: event.target.value
                    }))
                  }
                />
              </FormControl>

              {timeToMinutes(draftConfig.finishHour) <= timeToMinutes(draftConfig.startHour) && (
                <Box bg="red.50" color="red.600" p={3} borderRadius="md" w="100%">
                  O horário final precisa ser maior que o horário inicial.
                </Box>
              )}

              <Box bg="blue.50" color="blue.700" p={3} borderRadius="md" w="100%">
                <Text fontSize="sm">
                  Essa configuração será salva neste navegador e continuará disponível mesmo após o
                  usuário sair do sistema.
                </Text>
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancelar
            </Button>

            <Button
              colorScheme="blue"
              onClick={handleSaveConfig}
              isDisabled={
                timeToMinutes(draftConfig.finishHour) <= timeToMinutes(draftConfig.startHour)
              }
            >
              Salvar configuração
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}
